import React from 'react';
import {
  injectIntl,
  WrappedComponentProps,
  FormattedMessage
} from '@kyruus/intl';
import { ProviderMatchProviderReview } from '@kyruus/types';
import Star from '@kyruus/react-ikons/Star';

import {
  ReviewHeader,
  StarsContainer,
  StarContainer,
  DateContainer,
  BodyContainer,
  NoRating
} from './styles';
import SearchTermHighlighter from '../search-term-highlighter';
import messages from '../../../lib/messages';
import { getReviewStarCount, getReviewDateString } from '../../../lib/utils';
import { IndividualReviewRatings } from '../styles';

const Review = ({
  review,
  searchTerm,
  intl
}: {
  review: ProviderMatchProviderReview;
  searchTerm: string;
} & WrappedComponentProps): React.ReactElement => {
  const { review_date, body, author_display_name } = review;
  const parsedRating =
    typeof review.rating === 'string'
      ? parseFloat(review.rating)
      : review.rating;
  const rating = parsedRating <= 0 ? 0 : parsedRating;
  const starCount = getReviewStarCount(rating);
  const stars = [...Array(starCount).keys()].map((i) => (
    <StarContainer key={i}>
      <Star aria-hidden={true} />
    </StarContainer>
  ));

  const dateContainer = (
    <DateContainer>
      <FormattedMessage
        {...{
          ...messages.reviewsReviewDate,
          values: { date: getReviewDateString(review_date) }
        }}
      />
    </DateContainer>
  );

  const bodyContainer = (
    <BodyContainer itemProp="reviewBody">
      <SearchTermHighlighter searchTerm={searchTerm}>
        {body}
      </SearchTermHighlighter>
    </BodyContainer>
  );

  return (
    <div
      data-testid="individual-review"
      itemProp="review"
      itemScope={true}
      itemType="https://schema.org/Review"
    >
      <div
        itemProp="author"
        itemScope={true}
        itemType="https://schema.org/Person"
      >
        <meta itemProp="name" content={author_display_name || 'Anonymous'} />
      </div>

      <meta itemProp="dateCreated" content={getReviewDateString(review_date)} />

      {rating ? (
        <div>
          <ReviewHeader
            itemProp="reviewRating"
            itemScope={true}
            itemType="https://schema.org/Rating"
          >
            <StarsContainer>{stars}</StarsContainer>
            <IndividualReviewRatings
              itemProp="ratingValue"
              content={starCount.toString()}
            >
              <span className="sr-only">
                {`${intl.formatMessage(
                  messages.ariaStarRatingsIndividualReview,
                  {
                    rating: starCount
                  }
                )}`}
              </span>
              <span aria-hidden={true}>
                {`${starCount} ${intl.formatMessage(messages.ratingScale)}`}
              </span>
            </IndividualReviewRatings>
            {dateContainer}
            <meta itemProp="worstRating" content="1" />
            <meta itemProp="bestRating" content="5" />
          </ReviewHeader>
          {bodyContainer}
        </div>
      ) : (
        <div>
          <ReviewHeader>
            <NoRating>
              <FormattedMessage {...messages.reviewNoRating} />
            </NoRating>
            {dateContainer}
          </ReviewHeader>
          {bodyContainer}
        </div>
      )}
    </div>
  );
};

export default injectIntl(Review);
